/**
 * YUMIX — Day8PivotModal
 *
 * Auto-opens once when the user has acknowledged their Day-7 bilan AND
 * hasn't picked their Phase 2 path yet. Forces a single choice :
 *   A) ATHLÈTE   → enables the auto workout generator
 *   B) LIFESTYLE → focus on Sleep + Stress modules
 *
 * Persists the choice on user.day8_path so the dashboard can adapt.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { api, useAuth } from '../context/AppContext';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Dumbbell, Sparkles, Loader2, Heart, Moon, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const Day8PivotModal = ({ language = 'fr', user }) => {
  const fr = language.startsWith('fr');
  const { updateUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(null); // 'athlete' | 'lifestyle' | null

  // Eligibility :
  //  - account_age >= 7
  //  - day7_summary acknowledged
  //  - day8_path NOT yet picked (single source of truth = user.day8_path)
  //
  // Guard: once a choice is locked-in, the modal MUST never re-open. We rely
  // on `user.day8_path` from the auth context (refreshed inline by handlePick
  // via updateUser) so a successful pick instantly hides the modal even if
  // the parent `user` prop is still stale for one render tick.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (user?.day8_path) return; // already picked → never open
        const res = await api.get('/welcome-challenge/day7-summary');
        if (cancelled) return;
        if (!res.data?.available) return;
        const s = res.data.summary;
        if (!s?.acknowledged_at) return;
        // Eligible — open the pivot
        setOpen(true);
      } catch {
        /* silent */
      }
    })();
    return () => { cancelled = true; };
  }, [user?.day8_path]);

  const handlePick = async (path) => {
    setPicking(path);
    try {
      const res = await api.post('/welcome-challenge/day8-pivot', { path });
      // Refresh the in-memory user so the modal NEVER re-opens on any
      // subsequent Dashboard remount/render — fixes the "modal pops up
      // on every meal scan / nav transition" loop reported in beta.
      try {
        updateUser({ ...user, day8_path: path, day8_picked_at: res?.data?.picked_at });
      } catch { /* updateUser is best-effort; backend has the truth */ }
      toast.success(
        path === 'athlete'
          ? (fr ? 'Parcours Athlète activé' : 'Athlete path activated')
          : (fr ? 'Parcours Lifestyle activé' : 'Lifestyle path activated')
      );
      setOpen(false);
    } catch (err) {
      // Surface the real reason so beta-testers don't see a mute "Erreur".
      // Common causes here: 401 (session expired), 500 (backend), or a
      // network blip while the day8 modal sits over a busy meal-scan toast.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || '';
      console.error('[Day8 Pivot] failed:', status, detail, err);
      if (status === 401) {
        toast.error(fr ? 'Session expirée. Reconnecte-toi.' : 'Session expired. Sign in again.');
      } else if (!status) {
        toast.error(fr ? 'Réseau indisponible. Réessaie.' : 'Network unavailable. Retry.');
      } else {
        toast.error(fr ? `Erreur ${status} · ${detail}` : `Error ${status} · ${detail}`);
      }
      setPicking(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !picking) setOpen(false); }}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden border-orange-500/40 [&>button[type='button']]:hidden"
        // Force user to pick one — disable outside-click + Escape
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="day8-pivot-modal"
      >
        <DialogTitle className="sr-only">{fr ? 'Phase 2 · Choix de parcours' : 'Phase 2 · Path choice'}</DialogTitle>
        <DialogDescription className="sr-only">
          {fr ? 'Choisis ton parcours : Athlète ou Lifestyle.' : 'Pick your path: Athlete or Lifestyle.'}
        </DialogDescription>
        {/* Hero band */}
        <div className="bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 px-5 pt-7 pb-5 text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/80 font-bold">
            {fr ? 'Phase 2 · Choix de parcours' : 'Phase 2 · Path choice'}
          </p>
          <h2 className="font-heading text-2xl uppercase tracking-tight text-white mt-1">
            {fr ? 'Comment veux-tu progresser ?' : 'How do you want to progress?'}
          </h2>
          <p className="text-xs text-white/80 mt-1.5">
            {fr ? 'Un seul choix · adaptable plus tard' : 'One choice · adjustable later'}
          </p>
        </div>

        <div className="px-5 py-5 space-y-3">
          {/* ATHLÈTE */}
          <PathCard
            id="athlete"
            icon={Dumbbell}
            title={fr ? 'Athlète' : 'Athlete'}
            tagline={fr ? 'Plan d\'entraînement 100% auto' : 'Fully auto workout plan'}
            bullets={fr
              ? ['Génération IA basée sur ton équipement', 'Adaptation hebdo selon tes feedbacks', 'Suivi des sets/reps + progression']
              : ['AI generation based on your equipment', 'Weekly adaptation based on feedback', 'Sets/reps tracking + progression']}
            color="orange"
            onClick={() => handlePick('athlete')}
            picking={picking === 'athlete'}
            disabled={picking !== null && picking !== 'athlete'}
          />

          {/* LIFESTYLE */}
          <PathCard
            id="lifestyle"
            icon={Heart}
            title={fr ? 'Lifestyle' : 'Lifestyle'}
            tagline={fr ? 'Full Optimisation · Sommeil + Stress' : 'Full Optimization · Sleep + Stress'}
            bullets={fr
              ? ['Tracker sommeil avancé + recommandations', 'Module gestion du stress (cohérence cardiaque)', 'Routines guidées sans pression sportive']
              : ['Advanced sleep tracker + recommendations', 'Stress management module (HRV)', 'Guided routines without sport pressure']}
            color="violet"
            onClick={() => handlePick('lifestyle')}
            picking={picking === 'lifestyle'}
            disabled={picking !== null && picking !== 'lifestyle'}
          />

          <p className="text-center text-[10px] uppercase tracking-wider text-muted-foreground flex items-center justify-center gap-1.5 pt-2">
            <Sparkles className="w-3 h-3 text-orange-400" />
            {fr ? 'Tu pourras switcher dans Réglages plus tard' : 'You can switch in Settings later'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PathCard = ({ id, icon: Icon, title, tagline, bullets, color, onClick, picking, disabled }) => {
  const palette = {
    orange: 'from-orange-500/15 to-orange-600/5 border-orange-500/40 hover:border-orange-500/70',
    violet: 'from-violet-500/15 to-violet-600/5 border-violet-500/40 hover:border-violet-500/70',
  }[color];
  const iconBg = color === 'orange' ? 'bg-orange-500/20 text-orange-400' : 'bg-violet-500/20 text-violet-300';
  const cta    = color === 'orange' ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-violet-500 hover:bg-violet-600 text-white';

  return (
    <motion.div
      whileHover={{ scale: disabled ? 1 : 1.01 }}
      whileTap={{ scale: disabled ? 1 : 0.99 }}
      className={`rounded-xl border bg-gradient-to-br ${palette} transition-colors p-4`}
      data-testid={`day8-path-${id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${iconBg}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <h3 className="font-heading text-lg uppercase tracking-tight text-foreground">{title}</h3>
          <p className="text-[11px] text-muted-foreground">{tagline}</p>
        </div>
      </div>
      <ul className="space-y-1 mb-3">
        {bullets.map((b, i) => (
          <li key={i} className="text-xs text-foreground/85 flex items-start gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-400" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={onClick}
        disabled={disabled}
        className={`w-full font-heading uppercase tracking-wider text-[11px] ${cta}`}
        data-testid={`day8-pick-${id}`}
      >
        {picking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />...</> : (
          id === 'athlete' ? <><Dumbbell className="w-4 h-4 mr-2" />Activer Athlète</> : <><Moon className="w-4 h-4 mr-2" />Activer Lifestyle</>
        )}
      </Button>
    </motion.div>
  );
};

export default Day8PivotModal;
